import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { yupResolver } from '@hookform/resolvers/yup';
import { AddCircle, Edit } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { getCreateHolidayMutationOptions, getUpdateHolidayMutationOptions, isLoading, onValidationError, useCreateHolidayMutation, useSchoolYearQueryApi, useUpdateHolidayMutation, } from 'api';
import { ConfirmationDialog, DatePicker, LoadingWrapper, TextFormItem, } from 'components/custom';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDialogMode } from 'utils/dialog-form';
import { onSubmitError, yup } from 'utils/forms';
const SaveHolidayDialog = (props) => {
    const { activeHoliday, close } = props;
    const { t } = useTranslation('timetable', {
        keyPrefix: 'steps.vacation.cmp.SaveHolidayDialog',
    });
    const id = React.useId();
    const { modeCreate, open } = useDialogMode(activeHoliday);
    const schema = React.useMemo(() => yup.object({
        date: yup.date().label(t('dateField.label')).required(),
        name: yup.string().label(t('nameField.label')).required(),
    }), [t]);
    const { handleSubmit, control, formState: { errors, isSubmitting }, reset, } = useForm({
        resolver: yupResolver(schema),
    });
    React.useEffect(() => {
        reset(activeHoliday ?? {});
    }, [activeHoliday, reset]);
    const { schoolYearId = '' } = useParams();
    const schoolYearStatus = useSchoolYearQueryApi({ id: schoolYearId });
    const [createHoliday, createHolidaysStatus] = useCreateHolidayMutation();
    const [updateHoliday, updateHolidayStatus] = useUpdateHolidayMutation();
    const mutationStatus = modeCreate
        ? createHolidaysStatus
        : updateHolidayStatus;
    const onSuccess = () => {
        close();
        enqueueSnackbar(t('successMessage'));
    };
    const onSubmit = async (values) => {
        if (activeHoliday?.id) {
            return updateHoliday(getUpdateHolidayMutationOptions({
                ...values,
                id: activeHoliday.id,
            }))
                .then(onSuccess)
                .catch(onValidationError);
        }
        return createHoliday(getCreateHolidayMutationOptions({
            ...values,
            schoolYearId,
        }))
            .then(onSuccess)
            .catch(onValidationError);
    };
    const loading = isLoading([
        schoolYearStatus,
        updateHolidayStatus,
        createHolidaysStatus,
    ]);
    return (_jsx(ConfirmationDialog, { open: open, onClose: close, onTransitionExited: () => {
            mutationStatus.reset();
        }, titleProps: {
            Icon: modeCreate ? AddCircle : Edit,
            text: modeCreate ? t('addHolidayTitle') : t('editHolidayTitle'),
        }, cancelButtonProps: {
            onClick: close,
        }, confirmButtonProps: {
            disabled: isSubmitting || loading,
            form: id,
            type: 'submit',
        }, fullWidth: true, children: _jsx(LoadingWrapper, { status: schoolYearStatus, mutation: mutationStatus, children: _jsx("form", { id: id, onSubmit: (e) => {
                    handleSubmit(onSubmit)(e).catch(onSubmitError);
                }, children: _jsxs(Stack, { spacing: 2, children: [_jsx(Controller, { control: control, name: "name", render: ({ field }) => (_jsx(TextFormItem, { ...field, label: t('nameField.label'), placeholder: t('nameField.placeholder'), errorMessage: errors.name?.message })) }), _jsx(Controller, { control: control, name: "date", render: ({ field }) => (_jsx(DatePicker, { ...field, label: t('dateField.label'), errorMessage: errors.date?.message, minDate: schoolYearStatus.data?.startDate, maxDate: schoolYearStatus.data?.endDate })) })] }) }) }) }));
};
export default SaveHolidayDialog;
